import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { debounce } from "lodash";
import SearchIcon from "mdi-react/SearchIcon";
import FilterIcon from "mdi-react/FilterVariantIcon";
import SortAscIcon from "mdi-react/SortAscendingIcon";
import SortDescIcon from "mdi-react/SortDescendingIcon";
import CloseIcon from "mdi-react/CloseCircleIcon";
import "./ColumnSearchMenu.scss";

/**
 * Unified search, filter, and sort control.
 *
 * Layout:
 *   Row: [pills + clear all]  [Search input]  [Filters] [Sort]
 *   Disclosure 1 (Filters): [Search Columns] [Status] [Apply]
 *   Disclosure 2 (Sort): [Sort options] [Apply]
 *
 * Only one disclosure open at a time.
 */
const ColumnSearchMenu = ({
  columns,
  sortColumns,
  statuses = [],
  defaultStatuses = [],
  entrataStatuses = [],
  filter,
  setFilter,
  sortBy,
  onSort,
  disclosurePortalId,
  onPanelToggle,
}) => {
  const sortableColumns = sortColumns || columns;
  const [openPanel, setOpenPanel] = useState(null); // 'filters' | 'sort' | null
  const [keyword, setKeyword] = useState(filter.keyword || "");

  // Pending state for deferred apply
  const [pendingColumns, setPendingColumns] = useState([]);
  const [pendingStatuses, setPendingStatuses] = useState([]);
  const [pendingEntrataStatuses, setPendingEntrataStatuses] = useState([]);
  const [pendingSort, setPendingSort] = useState(null);

  useEffect(() => {
    setKeyword(filter.keyword || "");
  }, [filter.keyword]);

  // Sync pending state when a panel opens
  useEffect(() => {
    if (openPanel === "filters") {
      const currentCols = filter.search_columns
        ? filter.search_columns.split(",").map((c) => c.trim()).filter(Boolean)
        : [];
      setPendingColumns(currentCols);
      setPendingStatuses(filter.status || defaultStatuses);
      setPendingEntrataStatuses(
        filter.entrata_statuses
          ? filter.entrata_statuses.split(",").map((s) => s.trim()).filter(Boolean)
          : entrataStatuses.map((s) => s.name)
      );
    }
    if (openPanel === "sort") {
      setPendingSort(sortBy?.[0] || null);
    }
  }, [openPanel]);

  // Applied state
  const appliedColumns = filter.search_columns
    ? filter.search_columns.split(",").map((c) => c.trim()).filter(Boolean)
    : [];
  const allColumnsApplied = appliedColumns.length === 0;
  const allPendingColumnsSelected = pendingColumns.length === 0;

  const currentSortId = sortBy?.[0]?.id || null;
  const currentSortDesc = sortBy?.[0]?.desc || false;
  const currentSortLabel = currentSortId
    ? `${sortableColumns.find((c) => c.key === currentSortId)?.label || currentSortId} ${currentSortDesc ? "Z→A" : "A→Z"}`
    : null;

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((value) => {
      setFilter((prev) => ({ ...prev, keyword: value }));
    }, 1500),
    [setFilter]
  );

  const onKeywordChange = (e) => {
    setKeyword(e.target.value);
    if (!openPanel) {
      debouncedSearch(e.target.value);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !openPanel) {
      debouncedSearch.cancel();
      setFilter((prev) => ({ ...prev, keyword }));
    }
  };

  // Toggle panels — only one at a time
  const togglePanel = (panel) => {
    const next = openPanel === panel ? null : panel;
    // If closing a panel, apply any pending keyword change
    if (openPanel && !next && keyword !== filter.keyword) {
      setFilter((prev) => ({ ...prev, keyword }));
    }
    setOpenPanel(next);
    if (onPanelToggle) onPanelToggle(next !== null);
  };

  // Pending column toggles
  const togglePendingColumn = (key) => {
    if (allPendingColumnsSelected) {
      setPendingColumns([key]);
    } else if (pendingColumns.includes(key)) {
      setPendingColumns(pendingColumns.filter((c) => c !== key));
    } else {
      setPendingColumns([...pendingColumns, key]);
    }
  };

  // Pending status toggles
  const togglePendingStatus = (name) => {
    if (pendingStatuses.includes(name)) {
      const next = pendingStatuses.filter((s) => s !== name);
      setPendingStatuses(next.length === 0 ? defaultStatuses : next);
    } else {
      setPendingStatuses([...pendingStatuses, name]);
    }
  };

  // Pending Entrata status toggles — minimum-one constraint, same as Status filter
  const togglePendingEntrataStatus = (name) => {
    if (pendingEntrataStatuses.includes(name)) {
      const next = pendingEntrataStatuses.filter((s) => s !== name);
      setPendingEntrataStatuses(next.length === 0 ? entrataStatuses.map((s) => s.name) : next);
    } else {
      setPendingEntrataStatuses([...pendingEntrataStatuses, name]);
    }
  };

  // Apply filters
  const applyFilters = () => {
    const allKeys = columns.map((c) => c.key);
    const isAllCols = pendingColumns.length === allKeys.length && allKeys.every((k) => pendingColumns.includes(k));

    setFilter((prev) => {
      const next = {
        ...prev,
        keyword,
        search_columns: isAllCols || pendingColumns.length === 0 ? "" : pendingColumns.join(","),
        status: pendingStatuses,
      };
      const allEntrataSelected =
        entrataStatuses.length > 0 &&
        pendingEntrataStatuses.length === entrataStatuses.length &&
        entrataStatuses.every((s) => pendingEntrataStatuses.includes(s.name));
      if (pendingEntrataStatuses.length > 0 && !allEntrataSelected) {
        next.entrata_statuses = pendingEntrataStatuses.join(",");
      } else {
        delete next.entrata_statuses;
      }
      return next;
    });
    setOpenPanel(null);
    if (onPanelToggle) onPanelToggle(false);
  };

  // Apply sort
  const applySort = () => {
    setFilter((prev) => ({ ...prev, keyword }));
    if (pendingSort && onSort) {
      onSort(pendingSort.id, pendingSort.desc);
    }
    setOpenPanel(null);
    if (onPanelToggle) onPanelToggle(false);
  };

  // Clear all — reset everything
  const clearAll = () => {
    setPendingColumns([]);
    setPendingStatuses(defaultStatuses);
    setPendingEntrataStatuses([]);
    setPendingSort(null);
    setKeyword("");
    setFilter((prev) => {
      const next = { ...prev, keyword: "", search_columns: "", status: defaultStatuses };
      delete next.entrata_statuses;
      return next;
    });
    if (onSort) onSort(null, false);
    setOpenPanel(null);
    if (onPanelToggle) onPanelToggle(false);
  };

  const hasEntrataStatusFilter = Boolean(filter.entrata_statuses);
  const hasAnythingActive = !allColumnsApplied || currentSortId || keyword || hasEntrataStatusFilter;

  // Remove individual filter pill
  const removeColumnFilter = (key) => {
    const next = appliedColumns.filter((c) => c !== key);
    setFilter((prev) => ({
      ...prev,
      search_columns: next.length === 0 ? "" : next.join(","),
    }));
  };

  // Select pending sort
  const selectPendingSort = (key, desc) => {
    setPendingSort({ id: key, desc });
  };

  const portalEl = disclosurePortalId ? document.getElementById(disclosurePortalId) : null;

  return (
    <div className="column-search-menu">
      <div className="csm-bar">
        {/* Left: pills */}
        <div className="csm-bar-left">
          {!allColumnsApplied && appliedColumns.map((key) => {
            const col = columns.find((c) => c.key === key);
            return (
              <span key={key} className="csm-pill">
                {col?.label || key}
                <button type="button" onClick={() => removeColumnFilter(key)} className="csm-pill-x">
                  <CloseIcon size={12} />
                </button>
              </span>
            );
          })}
          {hasAnythingActive && (
            <button type="button" onClick={clearAll} className="csm-clear-btn">Clear all</button>
          )}
        </div>

        {/* Center: search input */}
        <div className="csm-bar-center">
          <div className="csm-search-wrapper">
            <input
              value={keyword}
              onChange={onKeywordChange}
              onKeyDown={onKeyDown}
              type="text"
              placeholder="Search"
              className="csm-search-input"
            />
            <SearchIcon className="csm-search-icon" size={18} />
          </div>
        </div>

        {/* Right: Filters + Sort buttons */}
        <div className="csm-bar-right">
          <button
            type="button"
            onClick={() => togglePanel("filters")}
            className={`csm-toggle-btn ${openPanel === "filters" ? "open" : ""} ${(!allColumnsApplied || hasEntrataStatusFilter) ? "active" : ""}`}
          >
            <FilterIcon size={16} />
            <span>Filters</span>
          </button>
          <button
            type="button"
            onClick={() => togglePanel("sort")}
            className={`csm-toggle-btn ${openPanel === "sort" ? "open" : ""} ${currentSortId ? "active" : ""}`}
          >
            <span>{currentSortLabel || "Sort"}</span>
            <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>

      {/* Filters disclosure — via portal */}
      {portalEl && createPortal(
        <div className={`csm-disclosure-wrapper ${openPanel === "filters" ? "open" : ""}`}>
          <div className="csm-disclosure-inner">
            <div className="csm-disclosure-columns">
              {/* Search Columns */}
              <div className="csm-disclosure-section">
                <div className="csm-section-title">Search Columns</div>
                {columns.map((col) => {
                  const isChecked = allPendingColumnsSelected || pendingColumns.includes(col.key);
                  return (
                    <label key={col.key} className="csm-checkbox-item">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => togglePendingColumn(col.key)}
                      />
                      <span>{col.label}</span>
                    </label>
                  );
                })}
              </div>

              {/* Status */}
              {statuses.length > 0 && (
                <div className="csm-disclosure-section">
                  <div className="csm-section-title">Status</div>
                  {statuses.map((status) => {
                    const isChecked = pendingStatuses.includes(status.name);
                    return (
                      <label key={status.name} className="csm-checkbox-item">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => togglePendingStatus(status.name)}
                        />
                        <span>{status.name}</span>
                      </label>
                    );
                  })}
                </div>
              )}

              {/* Entrata Status */}
              {entrataStatuses.length > 0 && (
                <div className="csm-disclosure-section">
                  <div className="csm-section-title">Entrata Status</div>
                  {entrataStatuses.map((status) => {
                    const isChecked = pendingEntrataStatuses.includes(status.name);
                    return (
                      <label key={status.name} className="csm-checkbox-item">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => togglePendingEntrataStatus(status.name)}
                        />
                        <span>{status.name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="csm-disclosure-actions">
              <button type="button" onClick={applyFilters} className="csm-apply-btn">Apply</button>
            </div>
          </div>
        </div>,
        portalEl
      )}

      {/* Sort disclosure — via portal */}
      {portalEl && createPortal(
        <div className={`csm-disclosure-wrapper ${openPanel === "sort" ? "open" : ""}`}>
          <div className="csm-disclosure-inner">
            <div className="csm-disclosure-columns">
              {sortableColumns.map((col) => (
                <div key={col.key} className="csm-disclosure-section">
                  <div className="csm-section-title">{col.label}</div>
                  <label
                    className={`csm-sort-item ${pendingSort?.id === col.key && !pendingSort?.desc ? "active" : ""}`}
                    onClick={() => selectPendingSort(col.key, false)}
                  >
                    <SortAscIcon size={14} />
                    <span>A→Z</span>
                  </label>
                  <label
                    className={`csm-sort-item ${pendingSort?.id === col.key && pendingSort?.desc ? "active" : ""}`}
                    onClick={() => selectPendingSort(col.key, true)}
                  >
                    <SortDescIcon size={14} />
                    <span>Z→A</span>
                  </label>
                </div>
              ))}
            </div>

            <div className="csm-disclosure-actions">
              <button type="button" onClick={applySort} className="csm-apply-btn">Apply</button>
            </div>
          </div>
        </div>,
        portalEl
      )}
    </div>
  );
};

export default ColumnSearchMenu;
